// src/pages/packingBatch/components/DispatchHandoverA4Document.jsx
// On-screen A4 preview of the Dispatched Orders courier handover sheet (mirrors utils/dispatchHandoverPdf.js).
import React, { useState } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import {
  paginateHandoverRows,
  getHandoverTitleDate,
  formatCod,
  formatStamp,
  getHandoverRowsPerColumn,
  getHandoverHeaderBottomY
} from '../../../utils/dispatchHandoverPdf';
import styles from './DispatchHandoverA4Document.module.css';

const DispatchHandoverA4Document = ({ data, businessName, logoUrl }) => {
  const { t } = useLocalization();
  const [logoRatio, setLogoRatio] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const rows = data?.handoverRows || [];
  const totalCod = data?.totalCod ?? rows.reduce((s, r) => s + Number(r.cod || 0), 0);
  const showLogo = !!logoUrl && !logoFailed;
  const { firstPerCol, restPerCol } = getHandoverRowsPerColumn(getHandoverHeaderBottomY(showLogo ? logoRatio : null));
  const pages = paginateHandoverRows(rows, firstPerCol, restPerCol);

  const renderTable = (list) => (
    <table className={styles.dataTable}>
      <colgroup>
        <col className={styles.colNo} />
        <col className={styles.colWaybill} />
        <col className={styles.colOrder} />
        <col className={styles.colCity} />
        <col className={styles.colCod} />
      </colgroup>
      <thead>
        <tr>
          <th>{t('courierUpload.colNo')}</th>
          <th>{t('courierUpload.colWaybill')}</th>
          <th>{t('courierUpload.colOrderNo')}</th>
          <th>{t('courierUpload.colCity')}</th>
          <th>{t('courierUpload.colCod')}</th>
        </tr>
      </thead>
      <tbody>
        {list.map(r => (
          <tr key={r.n}>
            <td>{r.n}</td>
            <td>{r.waybillId}</td>
            <td>{r.orderNumber}</td>
            <td title={r.city}>{r.city}</td>
            <td>{formatCod(r.cod)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className={styles.document}>
      {pages.map((page, pi) => (
        <div key={pi} className={`${styles.page} ${pi < pages.length - 1 ? styles.pageBreak : ''}`}>
          {pi === 0 && (
            <>
              {showLogo && (
                <div className={styles.logoWrap}>
                  <img
                    src={logoUrl}
                    alt=""
                    className={styles.logo}
                    onLoad={e => setLogoRatio(e.target.naturalWidth / e.target.naturalHeight || null)}
                    onError={() => setLogoFailed(true)}
                  />
                </div>
              )}
              <h2 className={styles.title}>{t('courierUpload.pdfTitle')} | {getHandoverTitleDate(data)}</h2>

              <div className={styles.infoGrid}>
                <div>
                  <p><strong>{t('courierUpload.companyName')}:</strong> {businessName}</p>
                  <p><strong>{t('courierUpload.courierCompanyLabel')}:</strong> {data?.courierCompany?.name}</p>
                </div>
                <div>
                  <p><strong>{t('courierUpload.courierRepresentative')}:</strong> ______________________</p>
                  <p>
                    <strong>{t('courierUpload.dateLabel')}:</strong> ____________
                    <span className={styles.spacer} />
                    <strong>{t('courierUpload.timeLabel')}:</strong> ____________
                  </p>
                </div>
              </div>

              <div className={styles.signatureSection}>
                <p>{t('courierUpload.dispatchConfirmText')}</p>
                <p>
                  <strong>{t('courierUpload.totalParcels')}:</strong> {rows.length}
                  <span className={styles.spacer} />
                  <strong>{t('courierUpload.totalCodAmount')}:</strong> {formatCod(totalCod)}
                </p>
                <p className={styles.sectionTitle}>{t('courierUpload.dispatchConfirmation')}</p>
                <p>{t('courierUpload.name')}: ________________________________</p>
                <p>{t('courierUpload.signature')}: ___________________________</p>
                <p className={styles.sectionTitle}>{t('courierUpload.parcelCollectionConfirmation')}</p>
                <p>{t('courierUpload.nameCourier')}: ________________________________</p>
                <p>{t('courierUpload.signatureCourier')}: ___________________________</p>
                <p className={styles.generatedOn}>{t('courierUpload.generatedOn')} {formatStamp(new Date())}</p>
              </div>
            </>
          )}

          <div className={styles.twoCol}>
            {renderTable(page.left)}
            {page.right.length > 0 ? renderTable(page.right) : <div />}
          </div>

          <div className={styles.pageNumber}>
            {t('common.pageOf', { page: pi + 1, total: pages.length })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DispatchHandoverA4Document;
